import React from 'react';
import { FormattedMessage } from 'react-intl';
import { rem } from 'polished';
import styled from 'styled-components';

import { wrapWithStore } from 'helpers/store';
import { breakpoints } from 'helpers/styles/constants';
import { container } from 'helpers/styles/mixins';
import GenericText from 'components/GenericText/GenericText';
import { GenericTitle, TitleMargin } from 'components/GenericTitle/GenericTitle';
import IconDws from 'components/Icons/IconDws';
import { MainTitles, SubTitle, Title } from 'components/MainTitles/MainTitles';
import LandingApp from 'LandingApp';

const Wrapper = styled.div`
  ${container()};
  margin-bottom: ${rem(90)};

  @media ${breakpoints.smUp} {
    margin-bottom: ${rem(140)};
  }

  & > section + section {
    margin-top: ${rem(50)};
  }

  a {
    text-decoration: underline;
  }

  p {
    line-height: 1.6;
  }
`;

const DwsImage = styled(IconDws)`
  display: block;
  margin: 0 auto ${rem(40)};
  width: ${rem(286)};
  height: ${rem(26)};

  @media ${breakpoints.smUp} {
    width: ${rem(429)};
    height: ${rem(39)};
  }
`;

const SectionTitle: React.FC = ({ children }) => (
  <GenericTitle as="h3" marginBottom={TitleMargin.Md} isMediumLg>
    {children}
  </GenericTitle>
);

const Subtitle: React.FC = ({ children }) => (
  <GenericTitle as="h4" marginBottom={TitleMargin.Sm} isMedium>
    {children}
  </GenericTitle>
);

const Content: React.FC = ({ children }) => <GenericText hasMarginBottomMd>{children}</GenericText>;

function SecurityStatementPage() {
  return (
    <LandingApp>
      <MainTitles>
        <Title>
          <DwsImage />
          <FormattedMessage id="nav.securityStatement" tagName="span" />
        </Title>

        <SubTitle>
          <FormattedMessage id="securityStatement.summary" />
        </SubTitle>
      </MainTitles>

      <Wrapper>
        <section>
          <Content>
            <FormattedMessage id="securityStatement.section1.content1" />
          </Content>
        </section>

        <section>
          <SectionTitle>
            <FormattedMessage id="securityStatement.section2.title" />
          </SectionTitle>

          <Content>
            <FormattedMessage id="securityStatement.section2.content1" />
          </Content>

          <Content>
            <FormattedMessage id="securityStatement.section2.content2" />
          </Content>
        </section>

        <section>
          <SectionTitle>
            <FormattedMessage id="securityStatement.section3.title" />
          </SectionTitle>

          <Content>
            <FormattedMessage id="securityStatement.section3.content1" />
          </Content>

          <Subtitle>
            <FormattedMessage id="securityStatement.section3.subtitle1" />
          </Subtitle>

          <Content>
            <FormattedMessage
              id="securityStatement.section3.content2"
              values={{
                link: (chunks: React.ReactNode) => (
                  <a href="https://aws.amazon.com/compliance/data-center/controls/">{chunks}</a>
                ),
              }}
            />
          </Content>

          <Subtitle>
            <FormattedMessage id="securityStatement.section3.subtitle2" />
          </Subtitle>

          <Content>
            <FormattedMessage id="securityStatement.section3.content3" />
          </Content>

          <Content>
            <FormattedMessage id="securityStatement.section3.content4" />
          </Content>

          <Subtitle>
            <FormattedMessage id="securityStatement.section3.subtitle3" />
          </Subtitle>

          <Content>
            <FormattedMessage id="securityStatement.section3.content5" />
          </Content>

          <Content>
            <FormattedMessage id="securityStatement.section3.content6" />
          </Content>

          <Content>
            <FormattedMessage id="securityStatement.section3.content7" />
          </Content>

          <Subtitle>
            <FormattedMessage id="securityStatement.section3.subtitle4" />
          </Subtitle>

          <Content>
            <FormattedMessage id="securityStatement.section3.content8" />
          </Content>

          <Subtitle>
            <FormattedMessage id="securityStatement.section3.subtitle5" />
          </Subtitle>

          <Content>
            <FormattedMessage id="securityStatement.section3.content9" />
          </Content>
        </section>

        <section>
          <SectionTitle>
            <FormattedMessage id="securityStatement.section4.title" />
          </SectionTitle>

          <Content>
            <FormattedMessage id="securityStatement.section4.content1" />
          </Content>
        </section>

        <section>
          <SectionTitle>
            <FormattedMessage id="securityStatement.section5.title" />
          </SectionTitle>

          <Content>
            <FormattedMessage id="securityStatement.section5.content1" />
          </Content>
        </section>
      </Wrapper>
    </LandingApp>
  );
}

export default wrapWithStore(SecurityStatementPage);
